import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CategoryService } from '../services/categoryService';
import type { Category, UpdateCategoryRequest, CreateSecondaryCategoryRequest, UpdateSecondaryCategoryRequest } from '../types/category';

const CategoryDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [category, setCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState<UpdateCategoryRequest>({});
  const [tagsInput, setTagsInput] = useState('');

  // Secondary category states
  const [showCreateSecondary, setShowCreateSecondary] = useState(false);
  const [editingSecondary, setEditingSecondary] = useState<string | null>(null);
  const [secondaryFormData, setSecondaryFormData] = useState<CreateSecondaryCategoryRequest>({
    name: '',
    description: '',
    displayOrder: 1,
    isActive: true
  });

  useEffect(() => {
    if (slug) {
      fetchCategoryDetails(slug);
    }
  }, [slug]);

  const fetchCategoryDetails = async (categorySlug: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await CategoryService.getCategoryBySlug(categorySlug);
      setCategory(response.data as Category);
      
      // Initialize form data
      setFormData({
        primaryCategory: (response.data as Category).primaryCategory,
        description: (response.data as Category).description,
        icon: (response.data as Category).icon,
        color: (response.data as Category).color,
        tags: (response.data as Category).tags,
      });
      
      setTagsInput((response.data as Category).tags.join(', '));
      
      document.title = `${(response.data as Category).primaryCategory} | Categories Admin`;
    } catch (err) {
      console.error('Error fetching category details:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setSuccess(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSuccess(false);
    if (category) {
      setFormData({
        primaryCategory: category.primaryCategory,
        description: category.description,
        icon: category.icon,
        color: category.color,
        tags: category.tags,
      });
      setTagsInput(category.tags.join(', '));
    }
  };

  const handleSave = async () => {
    if (!slug || !category) return;
    
    try {
      setIsSaving(true);
      setError(null);
      
      const response = await CategoryService.updateCategory(slug, formData);
      setCategory(response.data as Category);
      setIsEditing(false);
      setSuccess(true);
      
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating category:', err);
      setError(err instanceof Error ? err.message : 'Failed to update category');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field: keyof UpdateCategoryRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTagsChange = (value: string) => {
    setTagsInput(value);
    const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    setFormData(prev => ({ ...prev, tags }));
  };

  const handleDeleteCategory = async () => {
    if (!slug || !category) return;
    
    if (!window.confirm(`Are you sure you want to delete "${category.primaryCategory}"? This will also delete all secondary categories.`)) {
      return;
    }

    try {
      await CategoryService.deleteCategory(slug);
      navigate('/categories');
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete category');
    }
  };

  // Secondary category handlers
  const handleCreateSecondary = async () => {
    if (!slug || !secondaryFormData.name || !secondaryFormData.description) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      await CategoryService.createSecondaryCategory(slug, secondaryFormData);
      await fetchCategoryDetails(slug);
      setShowCreateSecondary(false);
      setSecondaryFormData({ name: '', description: '', displayOrder: 1, isActive: true });
    } catch (err) {
      console.error('Error creating secondary category:', err);
      setError(err instanceof Error ? err.message : 'Failed to create secondary category');
    }
  };

  const handleUpdateSecondary = async (secondarySlug: string) => {
    if (!slug || !secondaryFormData.name || !secondaryFormData.description) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      await CategoryService.updateSecondaryCategory(slug, secondarySlug, secondaryFormData);
      await fetchCategoryDetails(slug);
      setEditingSecondary(null);
      setSecondaryFormData({ name: '', description: '', displayOrder: 1, isActive: true });
    } catch (err) {
      console.error('Error updating secondary category:', err);
      setError(err instanceof Error ? err.message : 'Failed to update secondary category');
    }
  };

  const handleDeleteSecondary = async (secondarySlug: string, secondaryName: string) => {
    if (!slug) return;
    
    if (!window.confirm(`Are you sure you want to delete "${secondaryName}"?`)) {
      return;
    }

    try {
      await CategoryService.deleteSecondaryCategory(slug, secondarySlug);
      await fetchCategoryDetails(slug);
    } catch (err) {
      console.error('Error deleting secondary category:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete secondary category');
    }
  };

  const startEditSecondary = (secondary: any) => {
    setEditingSecondary(secondary.slug);
    setSecondaryFormData({
      name: secondary.name,
      description: secondary.description,
      displayOrder: secondary.displayOrder,
      isActive: secondary.isActive
    });
  };

  const cancelEditSecondary = () => {
    setEditingSecondary(null);
    setSecondaryFormData({ name: '', description: '', displayOrder: 1, isActive: true });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/categories" className="flex items-center text-sm text-gray-500 hover:text-gray-700">
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Categories
          </Link>
        </div>
        
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-500">Loading category details...</p>
        </div>
      </div>
    );
  }

  if (error && !category) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/categories" className="flex items-center text-sm text-gray-500 hover:text-gray-700">
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Categories
          </Link>
        </div>

        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>
      </div>
    );
  }

  if (!category) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Success notification */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                Category updated successfully!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error notification */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/categories" className="flex items-center text-sm text-gray-500 hover:text-gray-700">
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Categories
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{category.primaryCategory}</h1>
          {isEditing && (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
              Editing
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {!isEditing ? (
            <>
              <button
                onClick={handleDeleteCategory}
                className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete
              </button>
              <button
                onClick={handleEdit}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Edit
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Primary Category</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.primaryCategory || ''}
                      onChange={(e) => handleInputChange('primaryCategory', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <input
                      type="text"
                      value={category.primaryCategory}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
                      readOnly
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                  <input
                    type="text"
                    value={category.primarySlug}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
                    readOnly
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                {isEditing ? (
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                  />
                ) : (
                  <textarea
                    value={category.description}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
                    rows={3}
                    readOnly
                  />
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.icon || ''}
                      onChange={(e) => handleInputChange('icon', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{category.icon}</span>
                      <input
                        type="text"
                        value={category.icon}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
                        readOnly
                      />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  {isEditing ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.color || category.color}
                        onChange={(e) => handleInputChange('color', e.target.value)}
                        className="w-12 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        value={formData.color || category.color}
                        onChange={(e) => handleInputChange('color', e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-8 h-8 rounded border border-gray-300"
                        style={{ backgroundColor: category.color }}
                      />
                      <input
                        type="text"
                        value={category.color}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
                        readOnly
                      />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={tagsInput}
                    onChange={(e) => handleTagsChange(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter tags separated by commas"
                  />
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {category.tags.map((tag, index) => (
                      <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Secondary Categories */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Secondary Categories</h3>
              <button
                onClick={() => setShowCreateSecondary(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Secondary Category
              </button>
            </div>
            <div className="p-6">
              {category.secondaryCategories.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No secondary categories found. Click "Add Secondary Category" to get started.
                </div>
              ) : (
                <div className="space-y-4">
                  {category.secondaryCategories.map((secondary) => (
                    <div key={secondary._id} className="border border-gray-200 rounded-lg p-4">
                      {editingSecondary === secondary.slug ? (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                              <input
                                type="text"
                                value={secondaryFormData.name}
                                onChange={(e) => setSecondaryFormData(prev => ({ ...prev, name: e.target.value }))}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                              <input
                                type="number"
                                value={secondaryFormData.displayOrder}
                                onChange={(e) => setSecondaryFormData(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 1 }))}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                              value={secondaryFormData.description}
                              onChange={(e) => setSecondaryFormData(prev => ({ ...prev, description: e.target.value }))}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              rows={2}
                            />
                          </div>
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={cancelEditSecondary}
                              className="px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleUpdateSecondary(secondary.slug)}
                              className="px-3 py-1 text-sm text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">{secondary.name}</h4>
                            <p className="text-sm text-gray-500">{secondary.description}</p>
                            <div className="mt-1 flex items-center space-x-4 text-xs text-gray-400">
                              <span>Order: {secondary.displayOrder}</span>
                              <span>Status: {secondary.isActive ? 'Active' : 'Inactive'}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => startEditSecondary(secondary)}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteSecondary(secondary.slug, secondary.name)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Category Preview */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Preview</h3>
            <div className="text-center">
              <div 
                className="w-16 h-16 rounded-lg flex items-center justify-center text-white text-2xl font-medium mx-auto mb-3"
                style={{ backgroundColor: isEditing ? (formData.color || category.color) : category.color }}
              >
                {isEditing ? (formData.icon || category.icon) : category.icon}
              </div>
              <h4 className="text-lg font-semibold text-gray-900">
                {isEditing ? (formData.primaryCategory || category.primaryCategory) : category.primaryCategory}
              </h4>
              <p className="text-sm text-gray-500 mt-1">
                {isEditing ? (formData.description || category.description) : category.description}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Statistics</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Secondary Categories</span>
                <span className="text-sm font-medium text-gray-900">{category.secondaryCategories.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Tags</span>
                <span className="text-sm font-medium text-gray-900">{category.tags.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Status</span>
                <span className={`text-sm font-medium ${category.isActive ? 'text-green-600' : 'text-red-600'}`}>
                  {category.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Created</span>
                <span className="text-sm font-medium text-gray-900">
                  {new Date(category.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Secondary Category Modal */}
      {showCreateSecondary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-30" onClick={() => setShowCreateSecondary(false)}></div>
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Add Secondary Category</h3>
              <button onClick={() => setShowCreateSecondary(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={secondaryFormData.name}
                  onChange={(e) => setSecondaryFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Smartphones"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={secondaryFormData.description}
                  onChange={(e) => setSecondaryFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="e.g., Mobile phones and accessories"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                <input
                  type="number"
                  value={secondaryFormData.displayOrder}
                  onChange={(e) => setSecondaryFormData(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 1 }))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="1"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateSecondary(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSecondary}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryDetail;
